import { memo, useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  ToggleButton,
  Typography,
  styled,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  FormatBold,
  FormatClear,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RichTextEditor, RichTextEditorRef } from 'mui-tiptap';
import { type Editor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import DOMPurify from 'dompurify';
import CharacterCount from '@tiptap/extension-character-count';
import { AccessibleAutocomplete } from './AccessibleAutocomplete';
import { richTextExtensions } from './richTextExtensions';

export const StyledEditorWrapper = styled(Box)(({ theme }) => ({
  '& .MuiTiptap-FieldContainer-root': {
    backgroundColor: 'var(--color-white)',
  },
  '& .MuiTiptap-RichTextEditor-root': {
    backgroundColor: 'var(--color-white)',
    '&:focus-within': {
      borderColor: 'var(--color-scifree-teal)',
      borderWidth: '1px',
      boxShadow: 'none',
    },
    '& .MuiTiptap-RichTextContent-root': {
      minHeight: `calc(4 * ${theme.typography.body1.fontSize} * 1.6 + 28px)`,
      padding: '16.5px 14px',
      fontSize: theme.typography.body1.fontSize,
      fontFamily: theme.typography.fontFamily,
      lineHeight: 1.6,
      '& p.is-empty:first-of-type::before': {
        color: 'var(--color-dark-greyish-blue)',
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
      },
    },
  },
  '& .tiptap ul, & .tiptap ol': {
    paddingLeft: '1.5em',
  },
  '& .MuiTiptap-MenuBar-root, & .MuiTiptap-MenuBar': {
    color: 'var(--color-deep-ocean-blue) !important',
    padding: `${theme.spacing(1)} !important`,
    gap: `${theme.spacing(0.5)} !important`,
  },
  '& .MuiCollapse-entered.MuiTiptap-RichTextField-menuBar': {
    overflow: 'initial',
  },
  '& .MuiTiptap-MenuBar-root .MuiToggleButton-root, & .MuiTiptap-MenuBar .MuiToggleButton-root':
    {
      color: 'var(--color-deep-ocean-blue) !important',
      borderColor: 'var(--color-dark-greyish-blue) !important',
      '&:hover': { backgroundColor: 'var(--color-greyish-blue) !important' },
      '&:focus-visible': {
        outline: '2px solid var(--color-scifree-teal)',
        outlineOffset: '2px',
      },
      '&:disabled': { pointerEvents: 'none', opacity: 0.5 },
    },
  '& .MuiTiptap-MenuBar-root .MuiToggleButton-root.Mui-selected, & .MuiTiptap-MenuBar .MuiToggleButton-root.Mui-selected':
    {
      color: 'var(--color-white) !important',
      '&:hover': { backgroundColor: 'var(--color-deep-ocean-blue) !important' },
      '& .MuiSvgIcon-root': { color: 'var(--color-white) !important' },
    },
}));

const SrOnly = () => (
  <Box
    component="span"
    sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
  />
);

const toggleSx = {
  minWidth: 32,
  height: 32,
  '&.Mui-selected': {
    backgroundColor: 'var(--color-scifree-teal)',
    color: 'var(--color-white)',
    '&:hover': { backgroundColor: 'var(--color-deep-ocean-blue)' },
  },
};

export type RichTextEditorFieldProps = {
  value: string;
  onChange: (html: string) => void;
  id: string;
  maxLength?: number;
  disabled?: boolean;
  showLists?: boolean;
  error?: boolean;
  sx?: SxProps<Theme>;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  'aria-required'?: boolean;
  'aria-describedby'?: string;
  lang?: string;
};

export const RichTextEditorField = memo(function RichTextEditorField({
  value,
  onChange,
  id,
  maxLength = 2000,
  disabled = false,
  showLists = true,
  error = false,
  sx,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedBy,
  lang,
}: RichTextEditorFieldProps) {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorRef>(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [isEditorFocused, setIsEditorFocused] = useState(false);

  const charCountId = `${id}-charcount`;
  const combinedDescribedBy =
    [ariaDescribedBy, charCountId].filter(Boolean).join(' ') || undefined;

  // Sync external value changes (e.g. async data load) into the editor
  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (editor && !editor.isDestroyed && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value]);

  const extensions = [
    ...richTextExtensions,
    CharacterCount.configure({ limit: maxLength }),
  ];

  const errorSx = error
    ? {
        '& .MuiTiptap-RichTextEditor-root': {
          borderColor: 'var(--color-dark-grey-red)',
          '&:hover': { borderColor: 'var(--color-dark-grey-red)' },
          '&:focus-within': { borderColor: 'var(--color-dark-grey-red)' },
          '& .MuiTiptap-MenuBar-root': {
            borderColor: 'var(--color-dark-grey-red)',
          },
        },
        '&:hover .MuiTiptap-RichTextEditor-root': {
          borderColor: 'var(--color-dark-grey-red)',
        },
      }
    : {};

  const disabledSx = disabled
    ? {
        cursor: 'not-allowed',
        '& .MuiTiptap-FieldContainer-root': {
          backgroundColor: 'var(--color-light-greyish-blue)',
        },
        '& .MuiTiptap-RichTextEditor-root': {
          backgroundColor: 'var(--color-light-greyish-blue)',
        },
        '& .MuiTiptap-RichTextContent-root': {
          backgroundColor: 'var(--color-light-greyish-blue)',
          cursor: 'not-allowed',
        },
      }
    : {};

  return (
    <StyledEditorWrapper
      role="textbox"
      aria-multiline="true"
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      aria-required={ariaRequired}
      aria-describedby={combinedDescribedBy}
      sx={{ ...errorSx, ...disabledSx, ...sx }}
    >
      <RichTextEditor
        ref={editorRef}
        extensions={extensions}
        content={value || ''}
        editable={!disabled}
        onCreate={({ editor }: { editor: Editor }) => {
          setTimeout(() => {
            setCharacterCount(editor.storage.characterCount?.characters() ?? 0);
            const el = editor.view.dom;
            if (el) {
              el.setAttribute('role', 'textbox');
              el.setAttribute('aria-multiline', 'true');
              if (ariaLabel) el.setAttribute('aria-label', ariaLabel);
              if (ariaRequired) el.setAttribute('aria-required', 'true');
              if (disabled) el.setAttribute('aria-disabled', 'true');
              if (lang) el.setAttribute('lang', lang);
              el.style.paddingLeft = '15px';
              el.style.paddingRight = '15px';
              el.style.boxSizing = 'border-box';
            }
          }, 100);
          editor.on('focus', () => setIsEditorFocused(true));
          editor.on('blur', () => setIsEditorFocused(false));
        }}
        onUpdate={({ editor }: { editor: Editor }) => {
          const html = editor.getHTML();
          onChange(html);
          setCharacterCount(editor.storage.characterCount?.characters() ?? 0);
        }}
        editorProps={{
          handlePaste: (view: EditorView, event: ClipboardEvent) => {
            const data = event.clipboardData;
            if (!data) return false;
            const content =
              data.getData('text/html') || data.getData('text/plain');
            if (!content) return false;
            const clean = DOMPurify.sanitize(content, {
              ALLOWED_TAGS: [
                'p',
                'br',
                'strong',
                'b',
                'em',
                'i',
                'u',
                'h1',
                'h2',
                'h3',
                'ul',
                'ol',
                'li',
                'a',
              ],
              ALLOWED_ATTR: ['href'],
              FORBID_ATTR: [
                'style',
                'class',
                'id',
                'color',
                'font-size',
                'font-family',
                'font-weight',
                'background',
                'bgcolor',
                'face',
                'size',
              ],
              KEEP_CONTENT: true,
            });
            const ed = editorRef.current?.editor;
            if (ed) {
              ed.chain().focus().insertContent(clean).run();
              return true;
            }
            return false;
          },
        }}
        renderControls={() => {
          const editor = editorRef.current?.editor;

          const headingValue = editor?.isActive('heading', { level: 1 })
            ? 'h1'
            : editor?.isActive('heading', { level: 2 })
              ? 'h2'
              : editor?.isActive('heading', { level: 3 })
                ? 'h3'
                : 'none';
          const headingLabel = editor?.isActive('heading', { level: 1 })
            ? t('branding.richTextEditor.heading1')
            : editor?.isActive('heading', { level: 2 })
              ? t('branding.richTextEditor.heading2')
              : editor?.isActive('heading', { level: 3 })
                ? t('branding.richTextEditor.heading3')
                : t('branding.richTextEditor.normal');

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                p: { xs: 0.75, sm: 1 },
                flexWrap: 'nowrap',
                overflowX: 'auto',
                backgroundColor: 'var(--color-light-greyish-blue)',
                color: 'var(--color-deep-ocean-blue)',
              }}
            >
              <AccessibleAutocomplete
                value={{
                  value: headingValue,
                  label: headingLabel,
                  displayLabel: headingLabel,
                }}
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  if (newValue.value === 'none') {
                    editor?.chain().focus().setParagraph().run();
                  } else {
                    const level = parseInt(newValue.value.replace('h', '')) as
                      | 1
                      | 2
                      | 3;
                    editor?.chain().focus().toggleHeading({ level }).run();
                  }
                }}
                options={[
                  {
                    value: 'none',
                    label: t('branding.richTextEditor.normal'),
                    displayLabel: t('branding.richTextEditor.normal'),
                  },
                  {
                    value: 'h1',
                    label: t('branding.richTextEditor.heading1'),
                    displayLabel: t('branding.richTextEditor.heading1'),
                  },
                  {
                    value: 'h2',
                    label: t('branding.richTextEditor.heading2'),
                    displayLabel: t('branding.richTextEditor.heading2'),
                  },
                  {
                    value: 'h3',
                    label: t('branding.richTextEditor.heading3'),
                    displayLabel: t('branding.richTextEditor.heading3'),
                  },
                ]}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                disableClearable
                size="small"
                sx={{
                  width: 'auto',
                  minWidth: 110,
                  '& .MuiInputBase-root': { height: 32, minWidth: 110 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--color-white)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--color-dark-greyish-blue)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--color-deep-ocean-blue)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--color-scifree-teal)',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'var(--color-deep-ocean-blue) !important',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    padding: '0px 8px !important',
                    minWidth: 80,
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiAutocomplete-input': {
                    textAlign: 'left !important',
                    padding: '0px 8px !important',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '4px !important',
                  },
                }}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: 'var(--color-white)',
                      border: '1px solid var(--color-greyish-blue)',
                    },
                  },
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: option.value === 'none' ? 400 : 600,
                      py: 1,
                      px: 2,
                      color: 'var(--color-deep-ocean-blue)',
                    }}
                  >
                    {option.displayLabel}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    aria-label={
                      t('branding.richTextEditor.textStyle') as string
                    }
                    placeholder={
                      t('branding.richTextEditor.textStyle') as string
                    }
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': t(
                        'branding.richTextEditor.textStyle'
                      ) as string,
                      style: { color: 'inherit', fontWeight: 500 },
                    }}
                  />
                )}
              />

              <ToggleButton
                value="bold"
                selected={isEditorFocused && editor?.isActive('bold')}
                onChange={() => editor?.chain().focus().toggleBold().run()}
                disabled={!editor?.can().chain().focus().toggleBold().run()}
                aria-label={t('branding.richTextEditor.bold') as string}
                size="small"
                sx={toggleSx}
              >
                <FormatBold fontSize="small" />
                <SrOnly />
              </ToggleButton>

              <ToggleButton
                value="italic"
                selected={isEditorFocused && editor?.isActive('italic')}
                onChange={() => editor?.chain().focus().toggleItalic().run()}
                disabled={!editor?.can().chain().focus().toggleItalic().run()}
                aria-label={t('branding.richTextEditor.italic') as string}
                size="small"
                sx={toggleSx}
              >
                <FormatItalic fontSize="small" />
                <SrOnly />
              </ToggleButton>

              <ToggleButton
                value="underline"
                selected={isEditorFocused && editor?.isActive('underline')}
                onChange={() => editor?.chain().focus().toggleUnderline().run()}
                disabled={
                  !editor?.can().chain().focus().toggleUnderline().run()
                }
                aria-label={t('branding.richTextEditor.underline') as string}
                size="small"
                sx={toggleSx}
              >
                <FormatUnderlined fontSize="small" />
                <SrOnly />
              </ToggleButton>

              <ToggleButton
                value="link"
                selected={isEditorFocused && editor?.isActive('link')}
                onChange={() => {
                  const previousUrl = editor?.isActive('link')
                    ? editor.getAttributes('link').href || ''
                    : '';
                  const url = window.prompt(
                    t('branding.richTextEditor.enterUrl') as string,
                    previousUrl
                  );
                  if (url !== null) {
                    if (url === '') {
                      editor?.chain().focus().unsetLink().run();
                    } else {
                      editor?.chain().focus().setLink({ href: url }).run();
                    }
                  }
                }}
                aria-label={t('branding.richTextEditor.insertLink') as string}
                size="small"
                sx={toggleSx}
              >
                <LinkIcon fontSize="small" />
                <SrOnly />
              </ToggleButton>

              {showLists && (
                <>
                  <ToggleButton
                    value="bulletList"
                    selected={isEditorFocused && editor?.isActive('bulletList')}
                    onChange={() =>
                      editor?.chain().focus().toggleBulletList().run()
                    }
                    disabled={
                      !editor?.can().chain().focus().toggleBulletList().run()
                    }
                    aria-label={
                      t('branding.richTextEditor.bulletedList') as string
                    }
                    size="small"
                    sx={toggleSx}
                  >
                    <FormatListBulleted fontSize="small" />
                    <SrOnly />
                  </ToggleButton>

                  <ToggleButton
                    value="orderedList"
                    selected={
                      isEditorFocused && editor?.isActive('orderedList')
                    }
                    onChange={() =>
                      editor?.chain().focus().toggleOrderedList().run()
                    }
                    disabled={
                      !editor?.can().chain().focus().toggleOrderedList().run()
                    }
                    aria-label={
                      t('branding.richTextEditor.numberedList') as string
                    }
                    size="small"
                    sx={toggleSx}
                  >
                    <FormatListNumbered fontSize="small" />
                    <SrOnly />
                  </ToggleButton>
                </>
              )}

              <ToggleButton
                value="clear"
                selected={false}
                onChange={() =>
                  editor?.chain().focus().clearNodes().unsetAllMarks().run()
                }
                aria-label={
                  t('branding.richTextEditor.clearFormatting') as string
                }
                size="small"
                sx={toggleSx}
              >
                <FormatClear fontSize="small" />
                <SrOnly />
              </ToggleButton>

              <Box
                sx={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  minWidth: `${String(maxLength).length * 2 + 1}ch`,
                  justifyContent: 'flex-end',
                }}
              >
                <Typography
                  id={charCountId}
                  variant="caption"
                  component="span"
                  role="status"
                  aria-live="polite"
                  aria-label={`${characterCount} of ${maxLength} characters`}
                  sx={{
                    px: { xs: 0.5, sm: 1 },
                    whiteSpace: 'nowrap',
                    color:
                      characterCount > maxLength
                        ? 'var(--color-dark-grey-red)'
                        : 'var(--color-deep-ocean-blue)',
                  }}
                >
                  {characterCount}/{maxLength}
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
    </StyledEditorWrapper>
  );
});
